import { OpenPanel } from "@openpanel/web";
import { API_BASE_URL, OPENPANEL_API_URL, OPENPANEL_CLIENT_ID, OPENPANEL_DEBUG } from "../config";

const isBrowser = typeof window !== "undefined";
const isEnabled = Boolean(OPENPANEL_CLIENT_ID && isBrowser);

const noop = () => { };
const noopOp = {
    identify: noop,
    track: noop,
    setGlobalProperties: noop,
    clear: noop,
};
const SUPER_ADMIN_USER_ID = "1";

const getOpenPanel = () => {
    if (!isEnabled) return noopOp;
    return new OpenPanel({
        clientId: OPENPANEL_CLIENT_ID,
        apiUrl: OPENPANEL_API_URL,
        trackScreenViews: false,
        trackOutgoingLinks: false,
        trackAttributes: false,
    });
};

export const op = getOpenPanel();
let initialized = false;

let currentUserProperties = {};
let analyticsDisabledForCurrentUser = false;
let eventSequence = 0;
let sessionStarted = false;
let sessionEnded = false;
const recentEvents = new Map();
const RECENT_EVENT_TTL_MS = 1500;
const analyticsSessionId = [
    "portal",
    Date.now(),
    Math.random().toString(36).slice(2, 10),
].join(":");

export const initAnalytics = () => {
    if (initialized) return op;
    initialized = true;
    if (!sessionStarted) {
        sessionStarted = true;
        // "session_started", not "session_start": OpenPanel rejects session_start/
        // session_end as reserved names (HTTP 400, found live 2026-10-14 — the SDK
        // used to swallow the failure, so these events were silently lost). The
        // dashboard aggregator (openpanelAnalyticsService.js) accepts both spellings.
        trackEvent("session_started", {
            session_id: analyticsSessionId,
            page_path: isBrowser ? window.location.pathname : "",
            page_name: isBrowser ? document.title : "",
        });
    }
    if (OPENPANEL_DEBUG) {
        console.info("[OpenPanel] analytics initialized", {
            enabled: isEnabled,
            apiUrl: OPENPANEL_API_URL,
        });
    }
    return op;
};

const normalizeUserContext = (user = {}) => ({
    user_id: user.user_id ?? user.id ?? user.profileId ?? "",
    user_name: user.user_name ?? user.name ?? "",
    user_email: user.user_email ?? user.email ?? "",
    role_id: user.role_id ?? user.role?.role_id ?? "",

    customer_id: user.customer_id ?? user.cus_id ?? "",
    customer_name: user.customer_name ?? user.parent_customer_name ?? "",
    customer_number: user.customer_number ?? "",

    site_id: user.site_id ?? "",
    site_name: user.site_name ?? user.customer_site_name ?? "",
    site_number: user.site_number ?? user.customer_site_number ?? user.selectedSite ?? "",
    customer_site_number: user.customer_site_number ?? user.site_number ?? user.selectedSite ?? "",

    legal_entity: user.legal_entity ?? user.site_legal_entity ?? "",
    site_legal_entity: user.site_legal_entity ?? user.legal_entity ?? "",

    machine_id: user.machine_id ?? "",
    machine_name: user.machine_name ?? "",
    machine_number: user.machine_number ?? "",
    location: user.location ?? "",
    user_role: user.user_role ?? user.user_role_name ?? user.role?.role_name ?? (typeof user.role === "string" ? user.role : "") ?? "",
});

const normalizeAnalyticsContext = (context = {}) => ({
    user_id: context.user_id ?? context.id ?? context.profileId ?? "",
    user_name: context.user_name ?? context.user_display_name ?? context.name ?? "",
    user_email: context.user_email ?? context.email ?? "",
    role_id: context.role_id ?? context.role?.role_id ?? "",
    user_role:
        context.user_role ??
        context.user_role_name ??
        context.role?.role_name ??
        context.role?.role_type ??
        (typeof context.role === "string" ? context.role : "") ??
        "",

    customer_id: context.customer_id ?? context.cus_id ?? "",
    customer_name:
        context.customer_name ??
        context.parent_customer_name ??
        "",
    customer_number: context.customer_number ?? "",

    site_id: context.site_id ?? context.customer_site_id ?? "",
    site_name:
        context.site_name ??
        context.customer_site_name ??
        "",
    site_number:
        context.site_number ??
        context.customer_site_number ??
        context.selectedSite ??
        "",
    customer_site_number:
        context.customer_site_number ??
        context.site_number ??
        context.selectedSite ??
        "",

    legal_entity:
        context.legal_entity ??
        context.site_legal_entity ??
        "",
    site_legal_entity:
        context.site_legal_entity ??
        context.legal_entity ??
        "",

    currency: context.currency ?? "",
    machine_id: context.machine_id ?? "",
    machine_name: context.machine_name ?? "",
    machine_number: context.machine_number ?? "",
    location: context.location ?? "",
});
const isSuperAdminAnalyticsId = (value) => String(value ?? "").trim() === SUPER_ADMIN_USER_ID;

const isSuperAdminAnalyticsProperties = (properties = {}) =>
    isSuperAdminAnalyticsId(properties.user_id) ||
    isSuperAdminAnalyticsId(properties.profileId) ||
    isSuperAdminAnalyticsId(properties.id);

const syncGlobalProperties = () => {
    if (!isEnabled) return;
    op.setGlobalProperties(currentUserProperties);
};

const postEventToServer = (eventName, properties = {}) => {
    if (!isBrowser || !eventName) return Promise.resolve(false);
    if (analyticsDisabledForCurrentUser || isSuperAdminAnalyticsProperties(properties)) {
        return Promise.resolve(false);
    }

    const token = localStorage.getItem("token");
    if (!token || !API_BASE_URL) return Promise.resolve(false);

    const payload = JSON.stringify({
        eventName,
        properties,
    });

    return fetch(`${API_BASE_URL}/openpanel-analytics/track`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: payload,
        keepalive: payload.length < 60000,
    }).then((response) => {
        if (!response.ok && OPENPANEL_DEBUG) {
            console.warn("[OpenPanel] server track failed", response.status);
        }
        return response.ok;
    }).catch((error) => {
        if (OPENPANEL_DEBUG) {
            console.warn("[OpenPanel] server track failed", error);
        }
        return false;
    });
};

if (isBrowser) {
    window.addEventListener("beforeunload", () => {
        if (sessionEnded || analyticsDisabledForCurrentUser) return;
        sessionEnded = true;
        // "session_ended", not "session_end" — reserved in OpenPanel, see session_started above.
        trackEvent("session_ended", {
            session_id: analyticsSessionId,
            page_path: window.location.pathname || "",
            page_name: document.title || "",
        });
    });
}

const trackEventInBrowser = (eventName, properties = {}) => {
    if (!isEnabled || !eventName) return;
    if (analyticsDisabledForCurrentUser || isSuperAdminAnalyticsProperties(properties)) return;

    op.track(eventName, {
        profileId: properties.profileId || properties.user_id || undefined,
        ...properties,
    })?.catch?.((error) => {
        if (OPENPANEL_DEBUG) {
            console.warn("[OpenPanel] browser track failed", error);
        }
    });
};

const getEventFingerprint = (eventName, properties = {}) =>
    [
        eventName,
        properties.path || "",
        properties.page || "",
        properties.page_path || "",
        properties.page_label || "",
        properties.element_label || "",

        properties.machine_id || "",
        properties.machine_number || "",
        properties.model_name || "",
        properties.file_name || "",
        properties.product_id || "",
        properties.product_number || "",

        properties.customer_id || "",
        properties.customer_number || "",
        properties.site_id || "",
        properties.site_number || properties.customer_site_number || "",

        properties.search_term || "",
        properties.filter_source || "",
        properties.filter_value || "",
        properties.step || "",
        properties.banner_id || "",
        properties.banner_title || "",
        properties.promo_code || properties.promotion_code || "",
        properties.order_number || properties.order_key || "",
        properties.reason || "",
    ].join("|");

const shouldSkipDuplicateEvent = (eventName, properties = {}) => {
    const now = Date.now();
    const fingerprint = getEventFingerprint(eventName, properties);

    recentEvents.forEach((timestamp, key) => {
        if (now - timestamp > RECENT_EVENT_TTL_MS) {
            recentEvents.delete(key);
        }
    });

    const lastTimestamp = recentEvents.get(fingerprint);
    if (lastTimestamp && now - lastTimestamp < RECENT_EVENT_TTL_MS) {
        return true;
    }

    recentEvents.set(fingerprint, now);
    return false;
};

const createEventId = (eventName) => {
    eventSequence += 1;
    const userId = currentUserProperties.user_id || "anonymous";
    return [
        userId,
        eventName,
        Date.now(),
        eventSequence,
        Math.random().toString(36).slice(2, 10),
    ].join(":");
};

export const identifyUser = (user = {}) => {
    const profileId = user.profileId ?? user.user_id ?? user.id;
    if (!profileId) return;

    if (isSuperAdminAnalyticsProperties({ ...user, profileId })) {
        analyticsDisabledForCurrentUser = true;
        currentUserProperties = {};
        recentEvents.clear();
        if (isEnabled) op.clear();
        if (OPENPANEL_DEBUG) {
            console.info("[OpenPanel] skipped super admin analytics identify", {
                profileId: String(profileId),
            });
        }
        return;
    }

    analyticsDisabledForCurrentUser = false;
    currentUserProperties = normalizeUserContext(user);

    if (!isEnabled) return;

    if (OPENPANEL_DEBUG) {
        console.info("[OpenPanel] identify", {
            profileId: String(profileId),
            properties: currentUserProperties,
        });
    }

    op.identify({
        profileId: String(profileId),
        email: currentUserProperties.user_email || undefined,
        firstName: currentUserProperties.user_name || undefined,
        properties: currentUserProperties,
    });

    syncGlobalProperties();
};

export const setAnalyticsContext = (context = {}) => {
    if (analyticsDisabledForCurrentUser) return;

    currentUserProperties = {
        ...currentUserProperties,
        ...normalizeAnalyticsContext(context),
    };

    if (!isEnabled) return;

    if (OPENPANEL_DEBUG) {
        console.info("[OpenPanel] context", currentUserProperties);
    }

    syncGlobalProperties();
};

export const trackEvent = (eventName, metadata = {}) => {
    if (!eventName) return;
    if (analyticsDisabledForCurrentUser) return;

    const properties = {
        ...currentUserProperties,
        ...metadata,
    };

    if (isSuperAdminAnalyticsProperties(properties)) return;

    if (shouldSkipDuplicateEvent(eventName, properties)) return;

    const trackedProperties = {
        ...properties,
        event_id: properties.event_id || createEventId(eventName),
        session_id: properties.session_id || analyticsSessionId,
        source: properties.source || "portal",
    };

    if (OPENPANEL_DEBUG) {
        console.info("[OpenPanel] track", {
            eventName,
            metadata: trackedProperties,
        });
    }

    postEventToServer(eventName, trackedProperties).then((trackedByServer) => {
        if (!trackedByServer) {
            trackEventInBrowser(eventName, trackedProperties);
        }
    });
};

export const trackSearchPerformed = ({
    searchTerm,
    searchSource,
    pageName,
    resultCount = 0,
    ...metadata
} = {}) => {
    const term = String(searchTerm || "").trim();
    if (!term) return;

    const count = Number(resultCount) || 0;
    trackEvent("search_performed", {
        ...metadata,
        search_term: term,
        search_source: searchSource || metadata.search_source || "",
        page_name: pageName || metadata.page_name || (isBrowser ? document.title : ""),
        page_path: metadata.page_path || (isBrowser ? `${window.location.pathname}${window.location.search || ""}` : ""),
        result_count: count,
        has_results: count > 0,
        no_results: count === 0,
    });
};

export const resetAnalytics = () => {
    currentUserProperties = {};
    analyticsDisabledForCurrentUser = false;
    recentEvents.clear();
    if (isEnabled) {
        op.clear();
    }
};
